import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, type ImportLocalTestReportResult, type TestReport, type TestReportStatus } from '../api';
import { Button, Page } from '../ui';

interface Branch {
  branch: string;
  runs: number;
  lastRunAt: string;
}

function statusLabel(s: TestReportStatus): string {
  if (s === 'passed') return 'passou';
  if (s === 'failed') return 'falhou';
  return 'quebrou';
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s atrás`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}min atrás`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h atrás`;
  const d = Math.round(h / 24);
  return `${d}d atrás`;
}

function fmtDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return `${m}m${rs.toString().padStart(2, '0')}s`;
}

export function TestReports() {
  const { slug } = useParams<{ slug: string }>();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  const [runs, setRuns] = useState<TestReport[]>([]);
  const [selectedRun, setSelectedRun] = useState<TestReport | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportLocalTestReportResult | null>(null);

  function loadBranches(selectAfter?: string) {
    if (!slug) return;
    setLoading(true);
    api
      .listTestReportBranches(slug)
      .then(({ branches }) => {
        setBranches(branches);
        const target =
          selectAfter && branches.some((b) => b.branch === selectAfter)
            ? selectAfter
            : !selectedBranch && branches.length > 0
              ? branches[0]!.branch
              : selectedBranch;
        if (target) setSelectedBranch(target);
      })
      .catch((e) => setErr(e.status === 403 ? 'Sem permissao' : 'Falha ao carregar branches'))
      .finally(() => setLoading(false));
  }

  // Carrega branches na primeira render.
  useEffect(() => {
    loadBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  async function importLocal() {
    if (!slug || importing) return;
    setImporting(true);
    setErr(null);
    try {
      const result = await api.importLocalTestReport(slug);
      setImportResult(result);
      loadBranches(result.branch);
    } catch (e: unknown) {
      const err = e as { error?: string; status?: number };
      const code = err.error;
      const msg =
        code === 'project_local_repo_path_not_configured'
          ? 'Configure o caminho local do repo nas configurações antes de importar.'
          : code === 'no_allure_results_found'
            ? 'Nenhum diretório allure-results/ encontrado no repo local.'
            : code === 'allure_results_empty'
              ? 'allure-results/ está vazio (nenhum *-result.json).'
              : code ?? `Falha (${err.status ?? '?'})`;
      setErr(`Falha ao importar: ${msg}`);
    } finally {
      setImporting(false);
    }
  }

  // Carrega runs quando muda a branch selecionada.
  useEffect(() => {
    if (!slug || !selectedBranch) return;
    setLoading(true);
    api
      .listTestReports(slug, selectedBranch)
      .then(({ reports }) => {
        setRuns(reports);
        setSelectedRun(reports[0] ?? null);
      })
      .catch((e) => setErr(e.status === 403 ? 'Sem permissao' : 'Falha ao carregar runs'))
      .finally(() => setLoading(false));
  }, [slug, selectedBranch]);

  const showTrend = useMemo(
    () => selectedBranch === 'main' || selectedBranch === 'develop' || selectedBranch === 'master',
    [selectedBranch],
  );

  if (!slug) return null;

  return (
    <Page
      title="Test reports"
      subtitle="Histórico de runs por branch. CI envia automaticamente; rodadas locais podem ser importadas."
      actions={
        <Button variant="primary" onClick={importLocal} disabled={importing}>
          {importing ? 'Importando…' : 'Importar local'}
        </Button>
      }
      fluid
    >
      {err && <div className="ui-error-banner" style={{ marginBottom: 12 }}>{err}</div>}
      {importResult && (
        <div
          style={{
            marginBottom: 12,
            padding: 'var(--space-3) var(--space-4)',
            border: '1px solid var(--color-border)',
            background: 'var(--color-bg-2)',
            borderRadius: 'var(--radius-md)',
            fontSize: 13,
          }}
        >
          <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'baseline', flexWrap: 'wrap' }}>
            <strong>Import local concluído.</strong>
            <span className="text-muted">
              {importResult.usedDir} → {importResult.branch}/{importResult.runId} ·{' '}
              {importResult.summary.passedCount} passed · {importResult.summary.failedCount} failed ·{' '}
              {importResult.summary.brokenCount} broken · {importResult.summary.skippedCount} skipped ·{' '}
              {Math.round(importResult.summary.durationMs / 1000)}s
            </span>
            <button
              type="button"
              onClick={() => setImportResult(null)}
              style={{ background: 'transparent', border: 0, color: 'var(--color-fg-muted)', cursor: 'pointer' }}
              aria-label="fechar"
            >
              ×
            </button>
          </div>
          {!importResult.htmlGenerated && (
            <div style={{ marginTop: 6, color: 'var(--color-warn)', fontSize: 12 }}>
              HTML não foi gerado ({importResult.generateError}). A run aparece com counts mas sem viewer —
              instale <code>allure-commandline</code> no projeto pra gerar o relatório.
            </div>
          )}
          {importResult.candidates.length > 1 && (
            <div style={{ marginTop: 6, color: 'var(--color-fg-muted)', fontSize: 12 }}>
              Outros allure-results encontrados:{' '}
              {importResult.candidates
                .filter((c) => c !== importResult.usedDir)
                .map((c) => <code key={c} style={{ marginRight: 8 }}>{c}</code>)}
            </div>
          )}
        </div>
      )}
      <div className="tr-page">


      <div className="tr-layout">
        <aside className="tr-branches">
          <div className="tr-branches-title">branches</div>
          {branches.length === 0 && !loading && (
            <div className="tr-empty">Nenhum relatorio enviado ainda.</div>
          )}
          {branches.map((b) => (
            <button
              key={b.branch}
              type="button"
              className={`tr-branch ${b.branch === selectedBranch ? 'active' : ''}`}
              onClick={() => {
                setSelectedBranch(b.branch);
                setSelectedRun(null);
              }}
            >
              <div className="tr-branch-name">{b.branch}</div>
              <div className="tr-branch-meta">
                {b.runs} run{b.runs !== 1 ? 's' : ''} &middot; {timeAgo(b.lastRunAt)}
              </div>
            </button>
          ))}
        </aside>

        <section className="tr-runs">
          <div className="tr-runs-title">
            runs{selectedBranch ? ` em ${selectedBranch}` : ''}
          </div>
          {runs.length === 0 && selectedBranch && !loading && (
            <div className="tr-empty">Branch sem runs registradas.</div>
          )}
          {runs.map((r) => {
            const isActive = selectedRun?.id === r.id;
            return (
              <button
                key={r.id}
                type="button"
                className={`tr-run status-${r.status} ${isActive ? 'active' : ''}`}
                onClick={() => setSelectedRun(r)}
              >
                <div className="tr-run-top">
                  <span className={`tr-status-dot status-${r.status}`} />
                  <span className="tr-run-label">{statusLabel(r.status)}</span>
                  <span className="tr-run-sha">#{r.runId}</span>
                  {r.prNumber && <span className="tr-run-pr">PR #{r.prNumber}</span>}
                </div>
                <div className="tr-run-counts">
                  <span className="count-pass">{r.passedCount} OK</span>
                  {r.failedCount > 0 && <span className="count-fail">{r.failedCount} fail</span>}
                  {r.brokenCount > 0 && <span className="count-broken">{r.brokenCount} broken</span>}
                  {r.skippedCount > 0 && <span className="count-skip">{r.skippedCount} skip</span>}
                  <span className="tr-run-dur">{fmtDuration(r.durationMs)}</span>
                </div>
                <div className="tr-run-meta">
                  {r.sha.slice(0, 7)} &middot; {timeAgo(r.createdAt)}
                </div>
              </button>
            );
          })}
        </section>

        <section className="tr-viewer">
          {selectedRun ? (
            <>
              <div className="tr-viewer-toolbar">
                <span>
                  <strong>{selectedRun.branch}</strong> @ <code>{selectedRun.sha.slice(0, 7)}</code>
                  {' '}&middot;{' '}
                  <span className={`status-${selectedRun.status}`}>{statusLabel(selectedRun.status)}</span>
                </span>
                <div className="spacer" />
                <a
                  href={selectedRun.reportUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="tr-open-tab"
                >
                  abrir em nova aba
                </a>
                {selectedRun.ciRunUrl && (
                  <a href={selectedRun.ciRunUrl} target="_blank" rel="noreferrer" className="tr-open-ci">
                    ver CI
                  </a>
                )}
              </div>
              <iframe
                key={selectedRun.id}
                src={selectedRun.reportUrl}
                title={`Allure ${selectedRun.branch} ${selectedRun.runId}`}
                className="tr-iframe"
              />
              {showTrend && (
                <div className="tr-trend-hint">
                  O grafico de trend do Allure fica dentro do relatorio &mdash; aba &quot;Trends&quot; na sidebar esquerda do
                  relatorio acima. Para {selectedBranch}, a pipeline acumula o historico entre runs.
                </div>
              )}
            </>
          ) : (
            <div className="tr-empty tr-empty-center">
              {selectedBranch
                ? 'Selecione uma run na coluna da esquerda.'
                : 'Selecione uma branch.'}
            </div>
          )}
        </section>
      </div>
      </div>
    </Page>
  );
}
